import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
// import PhoneInput from "react-phone-number-input";
// import "react-phone-number-input/style.css";
import { Eye, EyeOff } from "lucide-react";
import { signUp } from "../../../Slices/sessionSlice";
import "./SignUp.css";

const customPhoneInputStyles = `
  .custom-phone-input .PhoneInputInput {
    border: none;
    outline: none;
    width: 100%;
  }
  .custom-phone-input {
    display: flex;
    align-items: center;
  }
`;

// Google logo SVG as a data URL - Commented out
// const GoogleLogo = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%234285F4' d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'/%3E%3Cpath fill='%2334A853' d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'/%3E%3Cpath fill='%23FBBC05' d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'/%3E%3Cpath fill='%23EA4335' d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'/%3E%3C/svg%3E";

function SignUp() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Google sign-up related state - Commented out
  // const [showGoogleSignupForm, setShowGoogleSignupForm] = useState(false);
  // const [googlePhoneNumber, setGooglePhoneNumber] = useState("");
  // const [googlePhoneError, setGooglePhoneError] = useState("");
  
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formValues, setFormValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    // phone: "", // Commented out
    password: "",
    confirmPassword: "",
    agreeToTerms: false,
  });

  const [formErrors, setFormErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    // phone: "", // Commented out
    password: "",
    confirmPassword: "",
    agreeToTerms: "",
  });

  // Google sign-up handlers - Commented out
  // const toggleGoogleSignupForm = () => {
  //   setShowGoogleSignupForm(!showGoogleSignupForm);
  // };

  // const handleGooglePhoneChange = (value) => {
  //   setGooglePhoneNumber(value || "");
  //   setGooglePhoneError("");
  // };

  // const handleGoogleSignUp = () => {
  //   if (!googlePhoneNumber) {
  //     setGooglePhoneError("Phone number is required");
  //     return;
  //   }
  //   // Handle Google sign-up with phone number
  //   // This would need to be implemented based on your backend API
  //   console.log("Google sign-up with phone:", googlePhoneNumber);
  // };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues({
      ...formValues,
      [name]: type === "checkbox" ? checked : value,
    });
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: "",
      });
    }
  };

  // Phone change handler - Commented out
  // const handlePhoneChange = (value) => {
  //   setFormValues({
  //     ...formValues,
  //     phone: value || "",
  //   });
  //   if (formErrors.phone) {
  //     setFormErrors({
  //       ...formErrors,
  //       phone: "",
  //     });
  //   }
  // };

  const handlePasswordVisibleToggle = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleConfirmPasswordVisibleToggle = () => {
    setConfirmPasswordVisible(!confirmPasswordVisible);
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    // First name validation
    if (!formValues.firstName.trim()) {
      errors.firstName = "First name is required";
      isValid = false;
    } else if (formValues.firstName.trim().length < 2) {
      errors.firstName = "First name must be at least 2 characters";
      isValid = false;
    }

    // Last name validation
    if (!formValues.lastName.trim()) {
      errors.lastName = "Last name is required";
      isValid = false;
    } else if (formValues.lastName.trim().length < 2) {
      errors.lastName = "Last name must be at least 2 characters";
      isValid = false;
    }

    // Email validation
    if (!formValues.email.trim()) {
      errors.email = "Email is required";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email.trim())) {
      errors.email = "Please enter a valid email address";
      isValid = false;
    }

    // Phone validation commented out
    // if (!formValues.phone) {
    //   errors.phone = "Phone number is required";
    //   isValid = false;
    // }

    // Password validation
    if (!formValues.password) {
      errors.password = "Password is required";
      isValid = false;
    } else if (formValues.password.length < 8) {
      errors.password = "Password must be at least 8 characters";
      isValid = false;
    }

    // Confirm password validation
    if (!formValues.confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
      isValid = false;
    } else if (formValues.password !== formValues.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
      isValid = false;
    }

    // Terms validation
    if (!formValues.agreeToTerms) {
      errors.agreeToTerms = "You must agree to the terms and conditions";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setFormErrors({});
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Note: The current signUp action doesn't include phone number
      // You may need to update the backend API to accept phone number
      const result = await dispatch(
        signUp({
          email: formValues.email.trim(),
          password: formValues.password,
          firstName: formValues.firstName.trim(),
          lastName: formValues.lastName.trim(),
          // phone: formValues.phone, // Uncomment when backend supports it
        })
      ).unwrap();

      // Check if email already exists (from successful response)
      if (result.isEmailAlreadyExists) {
        setFormErrors({
          firstName: "",
          lastName: "",
          email: "Email already exists. Please use log in or forgot password.",
          password: "",
          confirmPassword: "",
          agreeToTerms: "",
        });
        setLoading(false);
        return;
      }

      // If sign-up is successful, navigate to email verification page
      // Pass email as URL parameter for the verification page
      navigate(`/email-verification-pending?email=${encodeURIComponent(formValues.email.trim())}`);
    } catch (error) {
      console.error("Sign-up error:", error);
      
      // When using .unwrap() with rejectWithValue, the error is the value passed to rejectWithValue
      // Check if it's an email already exists error FIRST
      const isEmailExists = error?.isEmailAlreadyExists === true 
        || error?.responseData?.isEmailAlreadyExists === true
        || error?.response?.data?.isEmailAlreadyExists === true;
      
      // Set form errors
      const newErrors = {
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        confirmPassword: "",
        agreeToTerms: "",
      };
      
      if (isEmailExists) {
        // Show specific email already exists message
        newErrors.email = "Email already exists. Please use log in or forgot password.";
      } else {
        // Handle other API errors - extract error message from various possible locations
        let errorMessage = "An error occurred during sign up. Please try again.";
        
        // Check multiple possible error response structures
        // When using .unwrap() with rejectWithValue, error is the value passed to rejectWithValue
        if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.responseData?.error?.message) {
          errorMessage = error.responseData.error.message;
        } else if (error?.responseData?.message) {
          errorMessage = error.responseData.message;
        } else if (error?.response?.data?.error?.message) {
          errorMessage = error.response.data.error.message;
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }
        
        // Show error on email field
        newErrors.email = errorMessage;
      }
      
      setFormErrors(newErrors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{customPhoneInputStyles}</style>
      <div className="signup-container">
        <div className="signup-card">
          {/* Form header */}
          {/* <i className="fas fa-bolt text-primary mb-3 signup-icon" /> */}
          <h2 className="signup-title">Create account</h2>
          {/* <p className="signup-subtitle">
            After creating your account you can start using the platform
          </p> */}

          {/* Separator */}
          {/* <div className="signup-separator"></div> */}

          {/* Google sign-up section - Commented out */}
          {/* <div className="signup-google-section">
            {!showGoogleSignupForm ? (
              <button
                className="btn btn-google-signup"
                onClick={toggleGoogleSignupForm}
                type="button"
              >
                <img src={GoogleLogo} className="google-logo" alt="google logo" />
                <span>Sign Up with Google</span>
              </button>
            ) : (
              <div className="signup-google-header">
                <img src={GoogleLogo} className="google-logo-small" alt="google logo" />
                <span>Sign Up with Google</span>
              </div>
            )}

            {showGoogleSignupForm && (
              <div className="signup-google-form">
                <p className="signup-google-text">
                  Please enter your phone number to continue
                </p>
                <div className="mb-3">
                  <PhoneInput
                    international
                    defaultCountry="IN"
                    value={googlePhoneNumber}
                    onChange={handleGooglePhoneChange}
                    className={`custom-phone-input form-control ${
                      googlePhoneError ? "is-invalid" : ""
                    }`}
                  />
                  {googlePhoneError && (
                    <div className="invalid-feedback d-block">
                      {googlePhoneError}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleGoogleSignUp}
                  className="btn btn-primary btn-continue-google"
                >
                  Continue with Google
                </button>
              </div>
            )}
          </div> */}

          {/* Email sign-up section */}
          {/* <div className="signup-divider">
            <span>or Sign Up with email</span>
          </div> */}

          <form onSubmit={handleFormSubmit} noValidate>
            {/* Name fields */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="firstName" className="form-label">
                  First Name
                </label>
                <input
                  type="text"
                  className={`form-control ${
                    formErrors.firstName ? "is-invalid" : ""
                  }`}
                  id="firstName"
                  name="firstName"
                  value={formValues.firstName}
                  onChange={handleInputChange}
                  autoComplete="given-name"
                />
                {formErrors.firstName && (
                  <div className="invalid-feedback d-block">{formErrors.firstName}</div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="lastName" className="form-label">
                  Last Name
                </label>
                <input
                  type="text"
                  className={`form-control ${
                    formErrors.lastName ? "is-invalid" : ""
                  }`}
                  id="lastName"
                  name="lastName"
                  value={formValues.lastName}
                  onChange={handleInputChange}
                  autoComplete="family-name"
                />
                {formErrors.lastName && (
                  <div className="invalid-feedback d-block">{formErrors.lastName}</div>
                )}
              </div>
            </div>

            {/* Email field */}
            <div className="mb-3">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                type="email"
                className={`form-control ${
                  formErrors.email ? "is-invalid" : ""
                }`}
                id="email"
                name="email"
                value={formValues.email}
                onChange={handleInputChange}
                autoComplete="email"
              />
              {formErrors.email && (
                <div className="invalid-feedback d-block">
                  {formErrors.email}
                </div>
              )}
            </div>

            {/* Phone field - Commented out */}
            {/* <div className="mb-3">
              <label htmlFor="phone" className="form-label">
                Phone Number
              </label>
              <PhoneInput
                international
                defaultCountry="IN"
                value={formValues.phone}
                onChange={handlePhoneChange}
                className={`custom-phone-input form-control ${
                  formErrors.phone ? "is-invalid" : ""
                }`}
              />
              {formErrors.phone && (
                <div className="invalid-feedback">{formErrors.phone}</div>
              )}
            </div> */}

            {/* Password field */}
            <div className="mb-3">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <div className="password-input-wrapper">
                <input
                  type={passwordVisible ? "text" : "password"}
                  className={`form-control ${
                    formErrors.password ? "is-invalid" : ""
                  }`}
                  id="password"
                  name="password"
                  value={formValues.password}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={handlePasswordVisibleToggle}
                >
                  {passwordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {formErrors.password && (
                <div className="invalid-feedback d-block">{formErrors.password}</div>
              )}
            </div>

            {/* Confirm password field */}
            <div className="mb-4">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm Password
              </label>
              <div className="password-input-wrapper">
                <input
                  type={confirmPasswordVisible ? "text" : "password"}
                  className={`form-control ${
                    formErrors.confirmPassword ? "is-invalid" : ""
                  }`}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formValues.confirmPassword}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={handleConfirmPasswordVisibleToggle}
                >
                  {confirmPasswordVisible ? (
                    <EyeOff size={20} />
                  ) : (
                    <Eye size={20} />
                  )}
                </button>
              </div>
              {formErrors.confirmPassword && (
                <div className="invalid-feedback d-block">
                  {formErrors.confirmPassword}
                </div>
              )}
            </div>

            {/* Terms and conditions checkbox */}
            <div className="mb-4">
              <div className="form-check">
                <input
                  type="checkbox"
                  className={`form-check-input ${
                    formErrors.agreeToTerms ? "is-invalid" : ""
                  }`}
                  id="termsAndCondition"
                  name="agreeToTerms"
                  onChange={handleInputChange}
                  checked={formValues.agreeToTerms}
                />
                <label className="form-check-label" htmlFor="termsAndCondition">
                  I agree to the{" "}
                  terms of use{" "}
                  and{" "}
                  privacy policy{" "}
                </label>
              </div>
              {formErrors.agreeToTerms && (
                <div className="invalid-feedback d-block">
                  {formErrors.agreeToTerms}
                </div>
              )}
            </div>

            {/* Submit button */}
            <button
              type="submit"
              className="btn btn-primary btn-signup w-100 mb-3"
              disabled={loading}
            >
              {loading ? (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : null}
              {loading ? "Signing Up..." : "Sign Up"}
            </button>

            {/* Sign in link */}
            <p className="text-center signup-signin-text">
              Already have an account?{" "}
              <span
                className="signup-link signup-link-bold"
                onClick={() => {
                  navigate("/sign-in");
                }}
                style={{ cursor: "pointer" }}
              >
                Sign In
              </span>
            </p>
          </form>
        </div>
      </div>
    </>
  );
}

export default SignUp;
